import { Plus, MessageSquare, Edit2, Trash2, Loader2, Sparkles, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import ContinueLastConversation from '../ContinueLastConversation';
import { cn } from '../../lib/utils';

/**
 * AI Studio thread list — left sidebar showing every conversation in the
 * workspace with rename/delete affordances. On mobile this slides in as an
 * overlay drawer; on desktop it sits inline as a fixed-width column.
 *
 * All side effects flow in via callbacks — keeps the component a pure
 * presentational element so it can be unit-tested without mounting the full
 * AIHub state machine.
 *
 * Extracted from pages/AIHub.jsx.
 */
export function ThreadSidebar({
  threads,
  activeId,
  setActiveId,
  mobileSidebarOpen,
  setMobileSidebarOpen,
  createThread,
  deleteThread,
  renamingId,
  setRenamingId,
  renameValue,
  setRenameValue,
  saveRename,
  // List of thread_ids that currently have a pending background reply.
  // Used to render a small "thinking" spinner next to the row so the
  // user can see — at a glance — which other conversations are still
  // generating an answer while they're working in this one.
  pendingThreadIds = [],
  // Optional handler — when present and there are threads still named
  // "New chat" / lacking an auto-title, the sidebar shows a small
  // "Auto-title untitled chats" link the user can click to backfill
  // names across their whole conversation list.
  onBackfillTitles,
  backfillingTitles = false,
  // Optional per-thread "re-title from full conversation" handler. When
  // present, each row shows a small magic-wand button that re-asks the
  // LLM for a smarter title using the WHOLE chat history (not just the
  // first message). The id of the thread currently being retitled is
  // tracked via `retitlingId` so we can disable its button + spin its
  // icon.
  onRetitleThread,
  retitlingId = null,
}) {
  const pendingSet = new Set(pendingThreadIds || []);
  const untitledCount = (threads || []).filter(
    (t) => !t.title || t.title === 'New chat'
  ).length;
  return (
    <>
      {/* Mobile sidebar backdrop — covers the chat area behind the drawer */}
      {mobileSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-20"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="ai-hub-sidebar-backdrop"
        />
      )}

      {/* Sidebar — thread list */}
      <aside
        className={cn(
          'relative z-30 w-72 border-r border-zinc-200 dark:border-white/5 flex flex-col bg-white/95 dark:bg-black/90 backdrop-blur-xl',
          // Mobile: fixed overlay that slides in/out. Desktop: inline column.
          'fixed inset-y-0 left-0 transform transition-transform duration-200 lg:relative lg:translate-x-0',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
        data-testid="ai-hub-sidebar"
      >
        <div className="p-3 border-b border-zinc-200 dark:border-white/5">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-600 mb-2 px-1">
            Conversations
          </p>
          <Button
            onClick={() => { createThread(); setMobileSidebarOpen(false); }}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2"
            data-testid="ai-hub-new-chat-btn"
          >
            <Plus className="w-4 h-4" />
            New chat
          </Button>
          {/* Backfill action — only shown when the user actually has
              "New chat"-named threads, so it's invisible noise the rest
              of the time. */}
          {onBackfillTitles && untitledCount > 0 && (
            <button
              type="button"
              onClick={onBackfillTitles}
              disabled={backfillingTitles}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-[11px] text-zinc-600 dark:text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition disabled:opacity-50"
              data-testid="ai-hub-backfill-titles-btn"
            >
              {backfillingTitles
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Sparkles className="w-3 h-3" />}
              Auto-title {untitledCount} untitled chat{untitledCount === 1 ? '' : 's'}
            </button>
          )}
        </div>
        <ScrollArea className="flex-1">
          {/* pr-4 keeps the row content clear of the overlay scrollbar so the
              hover action cluster (retitle / rename / delete) doesn't get
              clipped when threads scroll. */}
          <div className="p-2 pr-4 space-y-1">
            {/* Resume last conversation — pinned at the top so users can jump
              * back into their most recent session across all chat surfaces. */}
            <ContinueLastConversation compact onPick={(id) => setActiveId(id)} />

            {threads.length === 0 && (
              <div className="text-center text-zinc-500 dark:text-zinc-500 text-xs py-8 px-3">
                No conversations yet. Start one above.
              </div>
            )}
            {threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => {
                  setActiveId(t.thread_id);
                  setMobileSidebarOpen(false); // close drawer on mobile after pick
                }}
                className={`group w-full text-left px-3 py-2.5 rounded-lg transition-colors flex items-start gap-2 ${
                  activeId === t.thread_id
                    ? 'bg-emerald-500/10 border border-emerald-500/20'
                    : 'hover:bg-black/5 dark:hover:bg-white/5 border border-transparent'
                }`}
                data-testid={`ai-hub-thread-${t.thread_id}`}
              >
                <MessageSquare className="w-4 h-4 mt-0.5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {renamingId === t.thread_id ? (
                    <Input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={saveRename}
                      onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                      className="h-7 text-sm bg-white dark:bg-zinc-900 border-zinc-200 dark:border-white/10"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div className="text-sm truncate text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5">
                        <span className="truncate">{t.title}</span>
                        {pendingSet.has(t.thread_id) && (
                          <Loader2
                            className="w-3 h-3 text-emerald-500 animate-spin flex-shrink-0"
                            aria-label="A reply is being generated for this conversation"
                            data-testid={`ai-hub-thread-pending-${t.thread_id}`}
                          />
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 dark:text-zinc-600 mt-0.5">
                        {pendingSet.has(t.thread_id) ? (
                          <span className="text-emerald-600 dark:text-emerald-400">
                            Studio AI thinking…
                          </span>
                        ) : (
                          `${t.message_count || 0} messages`
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                  {onRetitleThread && (t.message_count || 0) >= 2 && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Re-title from conversation"
                      title="Re-title from full conversation (AI)"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (retitlingId !== t.thread_id) onRetitleThread(t.thread_id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          if (retitlingId !== t.thread_id) onRetitleThread(t.thread_id);
                        }
                      }}
                      className={cn(
                        'p-1 rounded cursor-pointer',
                        retitlingId === t.thread_id
                          ? 'bg-emerald-500/15 cursor-wait'
                          : 'hover:bg-emerald-500/15',
                      )}
                      data-testid={`ai-hub-thread-retitle-${t.thread_id}`}
                    >
                      {retitlingId === t.thread_id ? (
                        <Loader2 className="w-3 h-3 text-emerald-500 animate-spin" />
                      ) : (
                        <Wand2 className="w-3 h-3 text-zinc-500 dark:text-zinc-400 hover:text-emerald-500 dark:hover:text-emerald-400" />
                      )}
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Rename"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenamingId(t.thread_id);
                      setRenameValue(t.title);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setRenamingId(t.thread_id);
                        setRenameValue(t.title);
                      }
                    }}
                    className="p-1 rounded hover:bg-black/10 dark:hover:bg-white/10 cursor-pointer"
                    data-testid={`ai-hub-thread-rename-${t.thread_id}`}
                  >
                    <Edit2 className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Delete"
                    onClick={(e) => { e.stopPropagation(); deleteThread(t.thread_id); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        deleteThread(t.thread_id);
                      }
                    }}
                    className="p-1 rounded hover:bg-red-500/20 cursor-pointer"
                    data-testid={`ai-hub-thread-delete-${t.thread_id}`}
                  >
                    <Trash2 className="w-3 h-3 text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </aside>
    </>
  );
}

export default ThreadSidebar;
